import React, { useState, useRef, useMemo } from 'react';
import './App.css';

const API_BASE = `/api`;

const plotOptions = [
  'Histogram',
  'Scatter Plot: Length vs Width (Outliers)',
  'Aspect Ratio vs. ECD Hexbin',
  'Correlation Heatmap',
  'PSD Curve',
  'PSD Curve (Log Scale)',
  'Binned Bar',
  'Boxplot',
  'ECD'
];

// Simple sortable table component
const SortableTable = ({ data, columns }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const sortedData = useMemo(() => {
    let sortableData = [...data];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getClassNamesFor = (name) => {
    if (!sortConfig.key) return;
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  return (
    <div className="apple-table-wrapper">
      <table className="apple-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => requestSort(col.key)}
                className={getClassNamesFor(col.key)}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <tr key={row.particle_id}>
              {columns.map((col) => (
                <td key={col.key}>{typeof row[col.key] === 'number' ? row[col.key].toFixed(2) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

function App() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedPlots, setSelectedPlots] = useState(
    plotOptions.reduce((obj, plot) => ({ ...obj, [plot]: true }), {})
  );
  const [modalImage, setModalImage] = useState(null);
  const [projectDetails, setProjectDetails] = useState({ project_code: '', client_name: '', date: new Date().toISOString().split('T')[0] });
  const [sampleDetails, setSampleDetails] = useState({ sample_code: '', sample_name: '' });
  const [labNotes, setLabNotes] = useState('');
  const [logo, setLogo] = useState(null);
  const [metadataError, setMetadataError] = useState('');
  const inputRef = useRef(null);
  const sieveSizes = [
    1000, 300, 212, 180, 150, 125, 106, 75, 53, 38, 25, 18, 12, 8, 6, 4, 3, 2, 1.5, 1, 0.7, 0.5
  ];
  const [sieveUserVals, setSieveUserVals] = useState(Array(sieveSizes.length).fill(''));
  const [sieveCompareResult, setSieveCompareResult] = useState(null);
  const [sieveCompareError, setSieveCompareError] = useState('');

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    setAnalysisResult(null);
    setError('');
  };

  const handleLogoChange = (event) => {
    setLogo(event.target.files[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      setSelectedFile(event.dataTransfer.files[0]);
      setAnalysisResult(null);
      setError('');
    }
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragleave' || event.type === 'dragover') {
      setDragActive(event.type !== 'dragleave');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first!');
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    setIsAnalyzing(true);
    try {
      const response = await fetch(`${API_BASE}/analyze/`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to analyze image.');
      }
      const data = await response.json();
      setAnalysisResult(data);
      setError('');
    } catch (err) {
      setError(err.message);
      setAnalysisResult(null);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handlePlotSelection = (plot) => {
    setSelectedPlots(prev => ({ ...prev, [plot]: !prev[plot] }));
  };

  const handleGenerateReport = async () => {
    if (!analysisResult) {
      setError('Please analyze an image first!');
      return;
    }
    // Check required metadata fields
    if (!projectDetails.project_code || !projectDetails.client_name || !projectDetails.date || !sampleDetails.sample_code || !sampleDetails.sample_name) {
      setMetadataError('Please fill in all project and sample details before generating the report.');
      return;
    } else {
      setMetadataError('');
    }
    const formData = new FormData();
    const reportData = {
      analysis_data: analysisResult.analysis_data,
      psd_data: analysisResult.psd_data,
      project_details: projectDetails,
      sample_details: sampleDetails,
      plot_selection: Object.keys(selectedPlots).filter(plot => selectedPlots[plot]),
      lab_notes: labNotes,
      sieve_comparison: sieveCompareResult ? {
        comparison: sieveCompareResult.comparison,
        mean_abs_error: sieveCompareResult.mean_abs_error,
        match: sieveCompareResult.match,
        plot_url: sieveCompareResult.plot_url
      } : null
    };
    formData.append('report_data', JSON.stringify(reportData));
    if (logo) {
      formData.append('logo', logo);
    }
    if (selectedFile) {
      formData.append('raw_image', selectedFile);
    }
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_BASE}/report/`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error('Failed to generate report.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = "particle_report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSieveInputChange = (i, val) => {
    // Remove % and whitespace, allow only numbers and dot
    let clean = val.replace(/[^\d.]/g, '');
    const arr = [...sieveUserVals];
    arr[i] = clean;
    setSieveUserVals(arr);
  };
  const handleSievePaste = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const raw = e.clipboardData.getData('Text');
    const cleaned = raw.replace(/%/g, '');

    // Try simple split first (works for newline/tab/comma/semicolon separated)
    let tokens = cleaned.split(/[\s,;]+/).filter(Boolean);
    tokens = tokens.map(v => v.replace(/[^\d.]/g, ''));

    const expected = sieveSizes.length;

    const parseConcatenated = (s, expectedCount) => {
      const out = [];
      let cur = '';
      let hasDot = false;
      let decimals = 0;
      for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch >= '0' && ch <= '9') {
          // digit
          cur += ch;
          if (hasDot) decimals += 1;
          // integer boundary heuristic: if we have at least 3 digits and next char starts another number
          if (!hasDot) {
            const next = s[i + 1];
            if (cur.length >= 3 && next && next >= '0' && next <= '9') {
              out.push(cur);
              cur = '';
              hasDot = false;
              decimals = 0;
              continue;
            }
          }
        } else if (ch === '.') {
          if (!hasDot) {
            hasDot = true;
            cur += ch;
            decimals = 0;
          } else {
            // unexpected second dot, close current token if possible
            if (cur) {
              out.push(cur.replace(/\.$/, ''));
              cur = '';
              hasDot = false;
              decimals = 0;
            }
          }
        }
        // decimal boundary: if we have at least one decimal digit and next char is a digit but we still want to end here when lookahead would create too many decimals
        if (hasDot && decimals >= 2) {
          const next = s[i + 1];
          if (next && next >= '0' && next <= '9') {
            out.push(cur);
            cur = '';
            hasDot = false;
            decimals = 0;
          }
        }
        if (out.length === expectedCount) break;
      }
      if (cur && out.length < expectedCount) out.push(cur.replace(/\.$/, ''));
      // Trim to expected length
      return out.slice(0, expectedCount);
    };

    let finalTokens = tokens;
    if (finalTokens.length !== expected) {
      // Remove all non-digit/dot and parse concatenated
      const compact = cleaned.replace(/[^\d.]/g, '');
      finalTokens = parseConcatenated(compact, expected);
    }

    // Normalize and set state
    const normalized = Array.from({ length: expected }, (_, i) => (finalTokens[i] ?? '').replace(/[^\d.]/g, ''));
    setSieveUserVals(normalized);
  };
  const handleCompareSieve = async () => {
    setSieveCompareError('');
    if (sieveUserVals.some(v => v === '' || isNaN(Number(v)))) {
      setSieveCompareError('Please fill in all % passing values (numbers only).');
      return;
    }
    if (!analysisResult) {
      setSieveCompareError('Please analyze an image first!');
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/compare_sieve/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sieve_sizes: sieveSizes,
          user_percent: sieveUserVals.map(Number),
          model_ecd: analysisResult.analysis_data.map(row => row.ecd_um)
        })
      });
      if (!response.ok) throw new Error('Comparison failed.');
      const data = await response.json();
      setSieveCompareResult(data);
    } catch (err) {
      setSieveCompareError(err.message);
    }
  };

  const tableColumns = [
    { key: 'particle_id', label: 'ID' },
    { key: 'length_um', label: 'Length (μm)' },
    { key: 'width_um', label: 'Width (μm)' },
    { key: 'aspect_ratio', label: 'Aspect Ratio' },
    { key: 'area_um2', label: 'Area (μm²)' },
    { key: 'circularity', label: 'Circularity' },
    { key: 'solidity', label: 'Solidity' }
  ];

  return (
    <>
      {modalImage && (
        <div className="apple-modal" onClick={() => setModalImage(null)}>
          <img src={modalImage} alt="Enlarged view" />
        </div>
      )}
      <header className="app-header">
        <div className="app-header-inner">
          {/* Place your logo file at frontend/public/backfill-pro-logo.png */}
          <img className="app-logo" src="/backfill-pro-logo.png" alt="Logo" onError={(e)=>{ e.currentTarget.style.display='none'; }} />
        </div>
      </header>
      <div className="apple-app-bg">
        <div className="apple-container">
          

          {/* 1. Report Options Section (always at the top) */}
          <div className="apple-report-section">
            <h3>Report Options</h3>
            <div className="apple-metadata-grid">
              <input type="text" placeholder="Project Code*" value={projectDetails.project_code} onChange={(e) => setProjectDetails({...projectDetails, project_code: e.target.value})} />
              <input type="text" placeholder="Client Name*" value={projectDetails.client_name} onChange={(e) => setProjectDetails({...projectDetails, client_name: e.target.value})} />
              <input type="date" value={projectDetails.date} onChange={(e) => setProjectDetails({...projectDetails, date: e.target.value})} />
              <input type="text" placeholder="Sample Code*" value={sampleDetails.sample_code} onChange={(e) => setSampleDetails({...sampleDetails, sample_code: e.target.value})} />
              <input type="text" placeholder="Sample Name*" value={sampleDetails.sample_name} onChange={(e) => setSampleDetails({...sampleDetails, sample_name: e.target.value})} />
            </div>
            {metadataError && <div className="apple-error">{metadataError}</div>}
            <textarea placeholder="Lab Notes..." rows="3" value={labNotes} onChange={(e) => setLabNotes(e.target.value)}></textarea>
          </div>

          {/* 2. Image Upload Button */}
          <form
            className={`apple-dropzone${dragActive ? ' drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => inputRef.current.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {selectedFile ? (
              <span className="apple-file-name">{selectedFile.name}</span>
            ) : (
              <span className="apple-drop-text">Drag & drop or click to select an image</span>
            )}
          </form>

          {/* 7. Analyze & Download Report Button (after all required fields and image) */}
          <button
            className="apple-upload-btn"
            onClick={handleUpload}
            disabled={!selectedFile || isAnalyzing}
            style={{ marginTop: 16, marginBottom: 24 }}
          >
            {isAnalyzing ? 'Analyzing...' : 'Analyze'}
          </button>

          {error && <div className="apple-error">{error}</div>}

          {/* Only show the rest after analysis is complete */}
          {analysisResult && analysisResult.analysis_data && (
            <>
              {/* 3. Debug Images */}
              {(analysisResult.grayscale_image || analysisResult.thresh_image) && (
                <div className="apple-debug-images">
                  <h3>Debug Images</h3>
                  <div className="apple-debug-img-row">
                    {analysisResult.grayscale_image && (
                      <div className="apple-debug-img-col">
                        <img
                          src={`data:image/png;base64,${analysisResult.grayscale_image}`}
                          alt="Grayscale"
                          className="apple-debug-img"
                          onClick={() => setModalImage(`data:image/png;base64,${analysisResult.grayscale_image}`)}
                        />
                        <div className="apple-debug-img-caption">Grayscale</div>
                      </div>
                    )}
                    {analysisResult.thresh_image && (
                      <div className="apple-debug-img-col">
                        <img
                          src={`data:image/png;base64,${analysisResult.thresh_image}`}
                          alt="Thresholded"
                          className="apple-debug-img"
                          onClick={() => setModalImage(`data:image/png;base64,${analysisResult.thresh_image}`)}
                        />
                        <div className="apple-debug-img-caption">Thresholded</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 4. Table Preview */}
              <SortableTable data={analysisResult.analysis_data} columns={tableColumns} />

              {/* 5. Sieve Set Comparison */}
              <div className="apple-report-section">
                <h3>Sieve Set Comparison</h3>
                <div className="apple-sieve-table-wrapper">
                  <table className="apple-sieve-table">
                    <thead>
                      <tr><th>Standardized Sieve Set Sizes (µm)</th><th>% Passing (user input)</th></tr>
                    </thead>
                    <tbody>
                      {sieveSizes.map((size, i) => (
                        <tr key={size}>
                          <td>{size}</td>
                          <td>
                            <input
                              type="text"
                              value={sieveUserVals[i]}
                              onChange={e => handleSieveInputChange(i, e.target.value)}
                              onPaste={i === 0 ? handleSievePaste : undefined}
                              style={{ width: 80 }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="apple-generate-btn" onClick={handleCompareSieve} style={{marginTop: 10}}>Compare to PSD</button>
                {sieveCompareError && <div className="apple-error">{sieveCompareError}</div>}
                {sieveCompareResult && (
                  <div className="apple-sieve-result">
                    <h4>Comparison Table</h4>
                    <table className="apple-sieve-table">
                      <thead>
                        <tr><th>Sieve (µm)</th><th>User %</th><th>Model %</th><th>Abs. Diff (%)</th></tr>
                      </thead>
                      <tbody>
                        {sieveCompareResult.comparison.map((row, i) => (
                          <tr key={i}>
                            <td>{row.sieve}</td>
                            <td>{row.user_pct.toFixed(1)}</td>
                            <td>{row.model_pct.toFixed(1)}</td>
                            <td>{row.abs_diff.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{marginTop: 8}}>
                      <b>Mean Absolute Error:</b> {sieveCompareResult.mean_abs_error.toFixed(2)}%
                      <br/>
                      <b>Validation:</b> {sieveCompareResult.match ? 'Match' : 'Mismatch'}
                    </div>
                    {sieveCompareResult.plot_url && (
                      <div style={{marginTop: 12}}>
                        <img src={sieveCompareResult.plot_url} alt="PSD Comparison" style={{maxWidth: 500}} />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 6. Plot Selection */}
              <div className="apple-report-section">
                <h3>Graphs to Include in Report</h3>
                <div className="apple-checkbox-group">
                  {plotOptions.map(plot => (
                    <div key={plot} className="apple-checkbox-wrapper">
                      <input
                        type="checkbox"
                        id={plot}
                        checked={selectedPlots[plot]}
                        onChange={() => handlePlotSelection(plot)}
                      />
                      <label htmlFor={plot}>{plot}</label>
                    </div>
                  ))}
                </div>
              </div>

              {/* 7. Download Report Button */}
              <button
                className="apple-generate-btn"
                onClick={handleGenerateReport}
                disabled={isGenerating}
                style={{marginBottom: 24}}
              >
                {isGenerating ? 'Generating...' : 'Download Report'}
              </button>
            </>
          )}
        </div>
    </div>
    </>
  );
}

export default App;
